import { motion } from "framer-motion";
import { behindTheScenes } from "../../data/portfolio";

/**
 * Editorial gallery — hero opening + true masonry middle + hero closing.
 *
 * The middle images flow through a CSS multi-column masonry with a single
 * consistent gutter (same horizontal + vertical spacing). This preserves
 * every photograph's native aspect ratio — no cropping, no lost faces,
 * no cut hands or camera rigs — while eliminating the empty vertical gaps
 * that a strict paired-grid produces with mixed aspects.
 */
export const BehindTheScenes = () => {
  const { index, title, subtitle, images } = behindTheScenes;
  if (!images || images.length === 0) return null;

  const opening = images[0];
  const middle = images.slice(1, images.length - 1);
  const closing = images.length > 1 ? images[images.length - 1] : null;

  const renderTile = (src, i, keyPrefix = "m") => (
    <motion.figure
      key={`${keyPrefix}-${i}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{
        duration: 1,
        delay: 0.05 * (i % 3),
        ease: [0.19, 1, 0.22, 1],
      }}
      className="mb-6 sm:mb-7 lg:mb-8 break-inside-avoid"
    >
      <div className="relative w-full bg-neutral-900 group">
        <img
          src={src}
          alt={`Behind the scenes photograph — Valeriy Rudenko film production, frame ${(i + 2).toString().padStart(2, "0")}`}
          loading="lazy"
          decoding="async"
          className="block w-full h-auto cinema-flat"
        />
      </div>
    </motion.figure>
  );

  return (
    <section
      id="behind"
      data-testid="behind-section"
      className="relative py-28 sm:py-40 lg:py-48 px-6 sm:px-10 lg:px-12 border-t border-white/10"
    >
      <div className="max-w-[1400px] mx-auto">
        {/* Header — editorial catalogue heading */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 1.1, ease: [0.19, 1, 0.22, 1] }}
          className="mb-16 sm:mb-20 lg:mb-24 text-center"
        >
          <p className="text-[10px] uppercase tracking-[0.4em] text-white/40 mb-6">
            <span style={{ color: "var(--accent-amber)" }}>{index}</span> — {title}
          </p>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.02] tracking-tighter text-white max-w-4xl mx-auto">
            <span className="italic opacity-90">On set,</span>
            <br />
            <span>process &amp; craft.</span>
          </h2>
          <p className="mt-8 text-sm sm:text-base text-white/50 font-sans max-w-xl mx-auto">
            {subtitle}
          </p>
        </motion.div>

        {/* Opening hero — full width, uncropped */}
        {opening && (
          <motion.figure
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
            className="mb-6 sm:mb-7 lg:mb-8"
          >
            <div className="relative w-full bg-neutral-900 group">
              <img
                src={opening}
                alt="Behind the scenes — Valeriy Rudenko directing on set, opening frame"
                loading="lazy"
                decoding="async"
                className="block w-full h-auto cinema-flat"
              />
            </div>
          </motion.figure>
        )}

        {/* Masonry middle — 1 col on mobile, 2 cols on desktop.
            Consistent gutter horizontal & vertical. */}
        {middle.length > 0 && (
          <div
            className="columns-1 md:columns-2 gap-6 sm:gap-7 lg:gap-8"
            style={{ columnFill: "balance" }}
          >
            {middle.map((src, i) => renderTile(src, i))}
          </div>
        )}

        {/* Closing hero — full width, uncropped, natural editorial finale */}
        {closing && (
          <motion.figure
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
            className="mt-0"
          >
            <div className="relative w-full bg-neutral-900 group">
              <img
                src={closing}
                alt="Behind the scenes — Valeriy Rudenko and crew at the monitor, closing frame"
                loading="lazy"
                decoding="async"
                className="block w-full h-auto cinema-flat"
              />
            </div>
          </motion.figure>
        )}
      </div>
    </section>
  );
};
